import { Game } from './Game';
import { Input } from './Input';
import { Audio } from './Audio';
import Originals from './OriginalBoards.mjs';
import { ROWS, COLS, MAX_BOARDS, MAX_FILE_BYTES, MAX_TEXT_FILE_BYTES, normalizeBoard, newBoard } from './BoardPack.mjs';
import { loadLibrary, saveLibrary, makeId, removeBoard, parseLibraryImport, mergeImport, exportLibrary, sameBoard } from './Library.mjs';
import { LibraryUI } from './LibraryUI';
import { RevisionUI } from './RevisionUI';
import { AnalysisUI } from './AnalysisUI';
import { TestUI } from './TestUI';
import { writeBoardText } from './BoardText.mjs';
import { bindBoardKeyboard } from './BoardKeyboard.mjs';

const tiles = [['=', 'Floor'], ['H', 'Ladder'], ['|', 'Wall'], ['-', 'Vanishing floor'], ['^', 'Fire'], ['V', 'Generator'], ['*', 'Rock eater'], ['$', 'Treasure'], ['&', 'Statue'], ['.', 'Trampoline'], ['p', 'Player'], [' ', 'Eraser']];
const colors = { p: '#edf6d9', $: '#f4cb78', '&': '#e4b976', V: '#f2a18d', '^': '#f2a18d', '*': '#bda4e5', '.': '#9bcbd7' };
const $ = id => document.getElementById(id);
const clone = value => JSON.parse(JSON.stringify(value));

export const Workshop = {
    get boards() { return this.library.boards; },
    init() {
        this.library = { version: 4, boards: [{ id: makeId('board'), ...newBoard(), revisions: [] }], sets: [], selectedSetId: 'original', campaignId: 'original' };
        this.selected = 0;
        this.brush = '=';
        this.cursorOnly = false;
        this.cursor = { x: 0, y: 0 };
        this.history = [];
        this.restore();
        for (const [index, board] of Originals.entries()) {
            $('template').add(new Option(board.name, index));
        }
        for (const [char, name] of tiles) {
            const button = document.createElement('button');
            const symbol = document.createElement('b');
            symbol.textContent = char === ' ' ? '·' : char;
            button.append(symbol, document.createTextNode(name));
            button.setAttribute('aria-label', `${name} (${char === ' ' ? 'space' : char})`);
            button.dataset.tile = char;
            button.onclick = () => {
                this.brush = char; this.cursorOnly = false; this.typing = false; this.cursor.end = false;
                this.renderPalette(); $('board-grid').focus({ preventScroll: true });
            };
            $('palette').append(button);
        }
        $('move-cursor').onclick = () => {
            this.cursorOnly = !this.cursorOnly;
            this.typing = false;
            this.renderPalette();
        };
        $('workshop-tab').onclick = $('open-workshop').onclick = () => this.show(true);
        $('game-tab').onclick = () => this.show(false);
        $('resume-game').onclick = () => {
            if (Game.session) Game.session.paused = false;
            Input.consume(true);
            $('canvas').focus();
        };
        $('test-board').onclick = () => this.attempt(() => TestUI.start(this.current()));
        $('play-pack').onclick = () => this.show('library');
        $('new-board').onclick = () => this.add([newBoard(`Untitled board ${this.boards.length + 1}`)]);
        $('copy-template').onclick = () => {
            const board = clone(Originals[Number($('template').value)]);
            board.name += ' — remix';
            this.add([board]);
        };
        $('duplicate-board').onclick = () => {
            const board = clone(this.current());
            board.name = board.name.slice(0, 53) + ' (copy)';
            this.add([board]);
        };
        $('delete-board').onclick = () => this.attempt(() => {
            const updated = removeBoard(this.library, this.current().id);
            this.remember();
            this.library = updated;
            if (!this.boards.length) this.boards.push({ id: makeId('board'), ...newBoard() });
            this.selected = Math.min(this.selected, this.boards.length - 1);
            this.changed();
            this.notify('Board removed. Use Undo to restore it.');
        });
        $('undo').onclick = () => {
            const previous = this.history.pop();
            if (!previous) return;
            this.library = previous.library;
            this.selected = previous.selected;
            this.changed();
            this.notify('Last change undone.');
        };
        $('board-name').addEventListener('focus', () => this.remember());
        $('board-name').oninput = () => {
            this.current().name = $('board-name').value;
            this.persist(); this.renderList(); this.validate(); this.browser?.render();
        };
        $('import-pack').onclick = () => { this.pendingImportForPlay = false; $('import-file').click(); };
        $('import-board').onclick = () => { this.pendingImportForPlay = false; $('import-board-file').click(); };
        $('import-board-file').onchange = async event => {
            const file = event.target.files[0];
            if (!file) return;
            try {
                if (file.size > MAX_TEXT_FILE_BYTES) throw new Error('The text board file is too large. Maximum size is 2 KB (2,048 bytes).');
                if (!/\.txt$/i.test(file.name)) throw new Error('Choose a .txt board file.');
                this.importText(await file.text(), file.name);
            } catch (error) { this.notify(error.message); }
            event.target.value = '';
        };
        $('paste-pack').onclick = () => this.openJSON(true);
        $('import-file').onchange = async event => {
            const file = event.target.files[0];
            if (!file) return;
            try {
                const textBoard = /\.txt$/i.test(file.name);
                if (!textBoard && !/\.json$/i.test(file.name)) throw new Error('Only .txt and .json files are accepted.');
                if (file.size > (textBoard ? MAX_TEXT_FILE_BYTES : MAX_FILE_BYTES)) throw new Error(textBoard ? 'Text boards are limited to 2 KB (2,048 bytes).' : 'JSON collections are limited to 1 MB.');
                this.importText(await file.text(), file.name, this.pendingImportForPlay);
            } catch (error) { this.notify(error.message); }
            this.pendingImportForPlay = false;
            event.target.value = '';
        };
        $('import-file').addEventListener('cancel', () => { this.pendingImportForPlay = false; });
        $('save-board').onclick = () => this.attempt(() => {
            const board = this.current();
            // Preserve every cell, including trailing spaces and unfinished drafts.
            const url = URL.createObjectURL(new Blob([board.layout.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' }));
            const link = document.createElement('a');
            link.href = url;
            const name = board.name.replace(/[^a-z0-9 _-]/gi, '').trim().slice(0, 60) || 'board';
            link.download = name + '.txt';
            document.body.append(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            this.notify('Board text download started. This file contains the current board layout; use Back up library to keep revisions and Series.');
        });
        $('export-pack').onclick = () => this.attempt(() => {
            this.openJSON(false, exportLibrary(this.library));
        });
        $('close-pack-dialog').onclick = () => $('pack-dialog').close();
        $('pack-dialog').addEventListener('close', () => {
            if (this.exportURL) URL.revokeObjectURL(this.exportURL);
            this.exportURL = undefined;
        });
        $('copy-json').onclick = async () => {
            try {
                await navigator.clipboard.writeText($('pack-json').value);
                $('pack-dialog-status').textContent = 'JSON copied to clipboard.';
            } catch {
                $('pack-json').focus(); $('pack-json').select();
                $('pack-dialog-status').textContent = 'Press Ctrl+C (or Command+C) to copy the selected JSON.';
            }
        };
        $('load-json').onclick = () => {
            try {
                this.importText($('pack-json').value, 'Pasted set.json');
                $('pack-dialog').close();
            } catch (error) { $('pack-dialog-status').textContent = error.message; }
        };
        this.bindGrid();
        window.addEventListener('ladder-ready', () => {
            this.validate();
            this.browser.render();
            if (!$('compare-view').hidden) RevisionUI.render();
        });
        window.addEventListener('ladder-menu', () => {
            $('game-title').textContent = 'Play the original game, or make the next board your own.';
            $('game-title').hidden = false;
            $('game-description').hidden = true;
            $('game-pack-label').textContent = 'Original collection · 7 boards';
            $('resume-game').hidden = true;
        });
        LibraryUI.init(this);
        RevisionUI.init(this);
        AnalysisUI.init(this);
        TestUI.init(this);
        this.persist();
        this.render();
    },

    current() { return this.boards[this.selected]; },

    openJSON(importing, text = '') {
        $('pack-dialog-title').textContent = importing ? 'Import boards and level sets' : 'Export JSON';
        $('pack-dialog-help').textContent = importing ? 'Paste a board, level set, library backup, or older JSON pack. Existing boards and sets are kept.' : 'Download the file, or copy the JSON below and save it as ladder-library.json.';
        $('pack-json').value = text;
        $('pack-json').readOnly = !importing;
        $('download-json').hidden = $('copy-json').hidden = importing;
        $('load-json').hidden = !importing;
        $('pack-dialog-status').textContent = '';
        if (!importing) {
            this.exportURL = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
            $('download-json').href = this.exportURL;
        }
        $('pack-dialog').showModal();
    },

    restore() {
        try {
            this.library = loadLibrary(localStorage);
            if (!this.boards.length) this.boards.push({ id: makeId('board'), ...newBoard() });
            this.selected = Math.max(0, this.boards.findIndex(board => board.id === this.library.selectedBoardId));
        } catch {
            this.storageBlocked = true;
            $('storage-status').textContent = 'Saved library could not be read. Existing storage has been preserved; export new work.';
        }
    },

    persist() {
        this.library.selectedBoardId = this.current()?.id;
        if (this.storageBlocked) return;
        try {
            saveLibrary(localStorage, this.library);
            $('storage-status').textContent = 'Saved in this browser';
        } catch {
            $('storage-status').textContent = 'Browser saving unavailable. Export a backup.';
        }
        $('library-storage').textContent = $('storage-status').textContent;
    },

    remember() {
        this.typing = false;
        this.history.push({ library: clone(this.library), selected: this.selected });
        if (this.history.length > 40) this.history.shift();
        $('undo').disabled = false;
        $('library-undo').disabled = false;
    },

    changed() { this.typing = false; this.cursor.end = false; this.persist(); this.render(); },
    attempt(action) { try { action(); } catch (error) { this.notify(error.message); } },
    notify(message) {
        $('notice').textContent = message;
        $('notice').hidden = false;
        clearTimeout(this.noticeTimer);
        this.noticeTimer = setTimeout(() => { $('notice').hidden = true; }, 7000);
    },

    add(boards) {
        if (this.boards.length + boards.length > MAX_BOARDS) throw new Error(`The library can hold at most ${MAX_BOARDS} custom boards.`);
        this.remember();
        this.selected = this.boards.length;
        this.boards.push(...boards.map(board => ({ ...board, layout: [...board.layout], id: makeId('board'), revisions: [] })));
        if (this.browser) this.browser.previewId = this.current().id;
        this.changed();
    },

    importText(text, filename, forPlay = false) {
        const imported = parseLibraryImport(text, filename);
        const merged = mergeImport(this.library, imported, forPlay);
        this.remember();
        this.library = merged;
        this.selected = Math.max(0, this.boards.findIndex(board => board.id === merged.selectedBoardId));
        this.browser.previewId = merged.selectedBoardId;
        this.changed();
        this.show(forPlay ? false : 'library');
        this.notify(`Imported ${imported.boards.length} board(s). ${forPlay ? 'Your custom game is selected; choose Start game.' : 'Browse boards and arrange your level sets here.'}`);
    },

    show(view) {
        const testing = view === 'test';
        if (!testing) TestUI.leave();
        const editing = view === true || view === 'workshop';
        if (!editing) AnalysisUI.cancel('Validation canceled when leaving Workshop. Run it again to check this draft.');
        const exploring = view === 'library';
        const replaying = view === 'replays';
        const outsideGame = editing || exploring || replaying;
        if (outsideGame && Game.session) Game.session.paused = true;
        Game.workshopOpen = outsideGame;
        Input.suspended = outsideGame;
        if (Input.buffer) Input.consume(true);
        $('game-view').hidden = outsideGame || testing;
        $('test-view').hidden = !testing;
        $('workshop-view').hidden = !editing;
        $('library-view').hidden = !exploring;
        $('compare-view').hidden = !editing || !RevisionUI.isOpen;
        $('replays-view').hidden = !replaying;
        $('replays-tab').classList.toggle('active', replaying);
        $('replays-tab').setAttribute('aria-pressed', String(replaying));
        this.replays?.onViewChange(replaying);
        $('game-tab').classList.toggle('active', !outsideGame && !testing);
        $('library-tab').classList.toggle('active', exploring);
        $('workshop-tab').classList.toggle('active', editing);
        $('game-tab').setAttribute('aria-pressed', String(!outsideGame && !testing));
        $('library-tab').setAttribute('aria-pressed', String(exploring));
        $('workshop-tab').setAttribute('aria-pressed', String(editing));
        $('resume-game').hidden = !Game.session;
        this.browser.render();
        if (editing && RevisionUI.isOpen) RevisionUI.render();
        if (editing) $('board-grid').focus({ preventScroll: true });
        if (!outsideGame) $('canvas').focus();
    },

    play(boards, label, options) {
        if (!Game.ready) return;
        this.show(false);
        Audio.readyToPlay = true;
        Audio.ctx.resume();
        this.recordingSeriesName = label;
        Game.startSession(boards, options);
        $('game-title').textContent = label;
        $('game-title').hidden = false;
        $('game-description').textContent = 'Collect a treasure to advance to the next board.';
        $('game-description').hidden = false;
        $('game-pack-label').textContent = `${label} · ${boards.length} board${boards.length === 1 ? '' : 's'}`;
        $('resume-game').hidden = false;
        $('canvas').focus();
    },

    renderList() {
        $('board-list').replaceChildren();
        this.boards.forEach((board, index) => {
            const li = document.createElement('li');
            li.classList.toggle('selected', index === this.selected);
            const button = document.createElement('button');
            button.className = 'board-select';
            button.textContent = `${String(index + 1).padStart(2, '0')}  ${board.name || 'Untitled'}`;
            button.setAttribute('aria-pressed', String(index === this.selected));
            button.onclick = () => { this.selected = index; this.changed(); };
            li.append(button);
            $('board-list').append(li);
        });
        $('board-count').textContent = `${this.boards.length} / ${MAX_BOARDS}`;
        for (const id of ['new-board', 'copy-template', 'duplicate-board']) $(id).disabled = this.boards.length >= MAX_BOARDS;
    },

    renderPalette() {
        for (const button of $('palette').children) button.setAttribute('aria-pressed', String(!this.cursorOnly && button.dataset.tile === this.brush));
        $('move-cursor').setAttribute('aria-pressed', String(this.cursorOnly));
        const name = tiles.find(([char]) => char === this.brush)[1];
        $('brush-label').textContent = this.cursorOnly ? 'Move cursor only · Tap or drag without changing cells · Keyboard: type ASCII' : `Mouse brush: ${name} (${this.brush === ' ' ? 'space' : this.brush}) · Keyboard: type ASCII`;
    },

    typeText(text, separateUndo = false) {
        if (!text) return;
        this.attempt(() => {
            const result = writeBoardText(this.current().layout, this.cursor, text);
            if (!this.typing || separateUndo) this.remember();
            this.current().layout = result.layout;
            this.cursor = result.cursor;
            this.typing = !separateUndo;
            this.persist(); this.draw(); this.validate(); this.updateCoordinates();
        });
    },

    updateCoordinates() {
        $('coordinates').textContent = `Column ${this.cursor.x + 1} · Row ${this.cursor.y + 1}${this.cursor.end ? ' · End of board' : ''}`;
    },

    eraseText(backward) {
        this.remember();
        if (backward && !this.cursor.end) {
            const previous = Math.max(0, this.cursor.y * COLS + this.cursor.x - 1);
            this.cursor = { x: previous % COLS, y: Math.floor(previous / COLS) };
        }
        this.paint(this.cursor.x, this.cursor.y, ' ');
        this.persist(); this.updateCoordinates();
    },

    render() {
        this.renderList(); this.renderPalette();
        $('board-name').value = this.current().name;
        $('undo').disabled = !this.history.length;
        this.draw(); this.validate();
        this.browser?.render();
    },

    validate() {
        AnalysisUI.sync();
        const draft = this.current();
        const revisions = draft.revisions || [];
        const latest = revisions.at(-1);
        const match = [...revisions].reverse().find(revision => sameBoard(draft, revision));
        $('board-revision-status').textContent = !latest
            ? 'Editing draft · No saved revision yet'
            : match
                ? 'Editing draft · Matches revision ' + match.number + (match === latest ? ' (latest)' : ' · Latest saved: revision ' + latest.number)
                : 'Editing draft · Changes since revision ' + latest.number + ' · Save revision to keep this version';

        if (RevisionUI.isOpen) {
            if (RevisionUI.boardId !== this.current().id) RevisionUI.close(false);
            else if (!$('compare-view').hidden) RevisionUI.render();
        }
        try {
            const board = normalizeBoard(this.current(), this.selected);
            const generators = board.layout.join('').split('V').length - 1;
            $('validation').textContent = `Ready to test · ${generators} generator${generators === 1 ? '' : 's'} · ${generators ? `up to ${7 + generators} boulders on the first pass` : 'no boulders'}. Test your route to check that the treasure is reachable.`;
            $('validation').className = '';
            $('test-board').disabled = !Game.ready;
        } catch (error) {
            $('validation').textContent = error.message;
            $('validation').className = 'invalid';
            $('test-board').disabled = true;
        }
    },

    draw() {
        const ctx = $('board-grid').getContext('2d');
        ctx.fillStyle = '#101611'; ctx.fillRect(0, 0, 1264, 480);
        ctx.strokeStyle = '#223025'; ctx.lineWidth = 1;
        ctx.beginPath();
        for (let x = 0; x <= COLS; x++) { ctx.moveTo(x * 16 + .5, 0); ctx.lineTo(x * 16 + .5, 480); }
        for (let y = 0; y <= ROWS; y++) { ctx.moveTo(0, y * 24 + .5); ctx.lineTo(1264, y * 24 + .5); }
        ctx.stroke(); ctx.font = '18px Consolas, monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
            const char = this.current().layout[y]?.[x] || ' ';
            ctx.fillStyle = colors[char] || '#b5dd85';
            ctx.fillText(char, x * 16 + 8, y * 24 + 12);
        }
        ctx.strokeStyle = '#e2edcf'; ctx.lineWidth = 2;
        ctx.strokeRect(this.cursor.x * 16 + 1, this.cursor.y * 24 + 1, 14, 22);
        ctx.fillStyle = '#f4cb78';
        ctx.fillRect(this.cursor.x * 16 + 2, this.cursor.y * 24 + 20, 12, 3);
        // Keep the native input near the cell, so opening the keyboard can reveal
        // the actual editing position rather than scrolling to an off-screen input.
        $('board-keyboard').style.left = `${this.cursor.x / COLS * 100}%`;
        $('board-keyboard').style.top = `${this.cursor.y / ROWS * 100}%`;
    },

    paint(x, y, char) {
        const board = this.current();
        if (board.layout.length > ROWS || board.layout.some(row => row.length > COLS || /[^\x20-\x7e]/.test(row))) {
            this.notify('Fix the map dimensions or invalid characters in a .txt file, then import the corrected board.');
            return;
        }
        while (board.layout.length < ROWS) board.layout.push('');
        board.layout = board.layout.map(row => row.padEnd(COLS, ' '));
        if (char === 'p') board.layout = board.layout.map(row => row.replace(/p/g, ' '));
        board.layout[y] = board.layout[y].slice(0, x) + char + board.layout[y].slice(x + 1);
        this.cursor = { x, y };
        this.draw(); this.validate();
    },

    bindGrid() {
        const grid = $('board-grid');
        const keyboard = $('board-keyboard');
        const position = event => {
            const bounds = grid.getBoundingClientRect();
            return { x: Math.max(0, Math.min(COLS - 1, Math.floor((event.clientX - bounds.left) / bounds.width * COLS))), y: Math.max(0, Math.min(ROWS - 1, Math.floor((event.clientY - bounds.top) / bounds.height * ROWS))) };
        };
        grid.oncontextmenu = event => event.preventDefault();
        grid.onpointerdown = event => {
            if (event.button !== 0 && event.button !== 2) return;
            event.preventDefault();
            if (event.pointerType !== 'touch') grid.focus();
            grid.setPointerCapture(event.pointerId);
            this.typing = false;
            if ((event.shiftKey || this.cursorOnly) && event.button === 0) {
                this.movingCursor = true;
                this.cursor = position(event);
                this.draw(); this.updateCoordinates();
                return;
            }
            this.remember(); this.painting = event.button === 2 ? ' ' : this.brush;
            this.lastPoint = position(event);
            this.paint(this.lastPoint.x, this.lastPoint.y, this.painting);
        };
        grid.onpointermove = event => {
            const point = position(event);
            $('coordinates').textContent = `Column ${point.x + 1} · Row ${point.y + 1}`;
            if (this.movingCursor) {
                this.cursor = point;
                this.draw(); this.updateCoordinates();
                return;
            }
            if (this.painting === undefined) return;
            const start = this.lastPoint;
            const steps = Math.max(Math.abs(point.x - start.x), Math.abs(point.y - start.y), 1);
            for (let i = 1; i <= steps; i++) this.paint(Math.round(start.x + (point.x - start.x) * i / steps), Math.round(start.y + (point.y - start.y) * i / steps), this.painting);
            this.lastPoint = point;
        };
        const stop = () => {
            if (this.painting !== undefined) this.persist();
            this.painting = undefined; this.movingCursor = false;
        };
        grid.onpointerup = event => {
            stop();
            // Focus synchronously in the user's touch gesture; deferred focus is
            // not allowed to open the keyboard on iOS.
            if (event.pointerType === 'touch') keyboard.focus();
        };
        grid.onpointercancel = grid.onlostpointercapture = stop;
        grid.onblur = () => { this.typing = false; };
        grid.onpaste = event => {
            event.preventDefault();
            this.typeText(event.clipboardData.getData('text/plain'), true);
        };
        grid.onkeydown = event => {
            if (event.isComposing) return;
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            const movement = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
            if (movement) {
                event.preventDefault();
                this.typing = false; this.cursor.end = false;
                this.cursor.x = Math.max(0, Math.min(COLS - 1, this.cursor.x + movement[0]));
                this.cursor.y = Math.max(0, Math.min(ROWS - 1, this.cursor.y + movement[1]));
                this.draw();
            } else if (event.key === 'Enter' || event.key.length === 1) {
                event.preventDefault(); this.typeText(event.key === 'Enter' ? '\n' : event.key);
            } else if (['Delete', 'Backspace'].includes(event.key)) {
                event.preventDefault(); this.eraseText(event.key === 'Backspace');
            }
            this.updateCoordinates();
        };
        bindBoardKeyboard(keyboard, {
            insert: (text, separateUndo) => this.typeText(text, separateUndo),
            erase: backward => this.eraseText(backward),
            keydown: event => grid.onkeydown(event),
            blur: () => { this.typing = false; }
        });
    }
};
